import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const Container = styled.div.attrs({ className: 'app-container' })`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  font-weight: light;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: #A961ED;
    margin: 15px auto 0;
  }
`;

const CheckoutContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  
  @media (min-width: 992px) {
    grid-template-columns: 1.5fr 1fr;
  }
`;

const FormContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  padding: 30px;
  border-radius: 8px;
`;

const OrderSummaryContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.3);
  padding: 30px;
  border-radius: 8px;
  align-self: start;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid white;
  border-radius: 8px;
  background-color: transparent;
  color: white;
  
  &:focus {
    outline: none;
    border-color: #A961ED;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid white;
  border-radius: 8px;
  background-color: transparent;
  color: white;
  
  option {
    background-color: #181819;
  }
  
  &:focus {
    outline: none;
    border-color: #A961ED;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  margin-top: 5px;
  font-size: 0.9rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  margin-bottom: 30px;
  
  h3 {
    margin-bottom: 15px;
    position: relative;
    padding-bottom: 10px;
    font-size: 20px;
    font-weight: 300;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 30px;
      height: 2px;
      background-color: #A961ED;
    }
  }
`;

const SummaryTitle = styled.h3`
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 10px;
  font-size: 20px;
  font-weight: 300;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 30px;
    height: 2px;
    background-color: #A961ED;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-size: 0.95rem;
  
  .item-name {
    width: 70%;
  }
  
  .item-price {
    width: 30%;
    text-align: right;
    font-weight: 500;
  }
`;

const SummaryDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 15px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
  font-size: 1.2rem;
  font-weight: 600;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background-color: #A961ED;
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 30px;
  
  &:hover:not(:disabled) {
    background-color: #9050c9;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CardElement = styled.div`
  padding: 15px;
  border: 1px solid white;
  border-radius: 8px;
  background-color: transparent;
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 40px;
  background-color: rgba(0, 0, 0, 0.3);

  p {
    margin-bottom: 20px;
  }
`;

const ContinueShoppingLink = styled(Link)`
  display: inline-block;
  padding: 15px 30px;
  background-color: #A961ED;
  color: white;
  text-align: center;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #9050c9;
  }
`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    // Load cart items from localStorage
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      } else {
        setCartItems([]);
      }
    } catch (err) {
      console.error('Error fetching cart:', err);
      setCartItems([]);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear validation error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic validation for required fields
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zipCode) newErrors.zipCode = 'Zip code is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // In a real app, you would save shipping info and redirect to payment gateway
        // This is a simulation
        setTimeout(() => {
          // Redirect to payment page (would be payment gateway in real app)
          navigate('/payment');
        }, 1000);
      } catch (error) {
        console.error('Error processing checkout:', error);
        alert('There was an error processing your checkout. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const itemQuantity = item.quantity || 1;
      return total + (parseFloat(item.price) * itemQuantity);
    }, 0);
  };

  // Calculate shipping fee - 20 EUR if cart contains any printed items
  const calculateShipping = () => {
    if (!cartItems || cartItems.length === 0) return 0;

    // Check if any item in cart is printed (not digital)
    const hasPhysicalItems = cartItems.some(item =>
      item.printType && item.printType !== 'digital'
    );

    return hasPhysicalItems ? 20.00 : 0;
  };

  const calculateTotal = () => {
    // Return the subtotal plus shipping
    return calculateSubtotal() + calculateShipping();
  };

  return (
    <PageContainer>
      <Container>
        <SectionTitle>Checkout</SectionTitle>
        {cartItems.length === 0 ? (
          <EmptyCart>
            <p>Your cart is empty.</p>
            <ContinueShoppingLink to="/create">Start Designing Your Star Map</ContinueShoppingLink>
          </EmptyCart>
        ) : (
          <form onSubmit={handleSubmit}>
            <CheckoutContainer>
              <FormContainer>
                <FormSection>
                  <h3>Contact Information</h3>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                      />
                      {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                      />
                      {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                  </FormGroup>
                </FormSection>

                <FormSection>
                  <h3>Shipping Address</h3>
                  <FormGroup>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                    />
                    {errors.address && <ErrorMessage>{errors.address}</ErrorMessage>}
                  </FormGroup>

                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="city">City</Label>
                      <Input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                      />
                      {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="state">State / Province</Label>
                      <Input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                      />
                      {errors.state && <ErrorMessage>{errors.state}</ErrorMessage>}
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="zipCode">Zip / Postal Code</Label>
                      <Input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                      />
                      {errors.zipCode && <ErrorMessage>{errors.zipCode}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="country">Country</Label>
                      <Select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                      >
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                        <option value="FR">France</option>
                        <option value="DE">Germany</option>
                      </Select>
                    </FormGroup>
                  </FormRow>
                </FormSection>
              </FormContainer>

              <OrderSummaryContainer>
                <SummaryTitle>Order Summary</SummaryTitle>

                {cartItems.map(item => (
                  <SummaryItem key={item.id}>
                    <div className="item-name">{item.name}</div>
                    <div className="item-price">€{parseFloat(item.price).toFixed(2)}</div>
                  </SummaryItem>
                ))}

                <SummaryDivider />

                <SummaryItem>
                  <div className="item-name">Subtotal</div>
                  <div className="item-price">€{calculateSubtotal().toFixed(2)}</div>
                </SummaryItem>

                {calculateShipping() > 0 && (
                  <SummaryItem>
                    <div className="item-name">Shipping</div>
                    <div className="item-price">€{calculateShipping().toFixed(2)}</div>
                  </SummaryItem>
                )}

                <SummaryDivider />

                <TotalRow>
                  <div>Total</div>
                  <div>€{calculateTotal().toFixed(2)}</div>
                </TotalRow>

                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
                </SubmitButton>
              </OrderSummaryContainer>
            </CheckoutContainer>
          </form>
        )}
      </Container>
    </PageContainer>
  );
};

export default CheckoutPage; 